
import type { RouteRecordRaw } from 'vue-router'

export const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/auth/LoginPage.vue'),
    meta: { layout: 'auth', titleKey: 'auth.login.title' },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/pages/auth/RegisterPage.vue'),
    meta: { layout: 'auth', titleKey: 'auth.register.title' },
  },
  {
    path: '/onboarding',
    name: 'onboarding-landing',
    component: () => import('@/pages/onboarding/OnboardingLanding.vue'),
    meta: { requiresAuth: true, layout: 'onboarding', titleKey: 'onboarding.pageTitle' },
  },
  {
    path: '/onboarding/ai',
    name: 'onboarding-ai',
    component: () => import('@/pages/onboarding/OnboardingChatPage.vue'),
    meta: { requiresAuth: true, layout: 'onboarding', titleKey: 'onboarding.pageTitle' },
  },
  {
    path: '/onboarding/manual',
    name: 'onboarding-manual',
    component: () => import('@/pages/onboarding/OnboardingPage.vue'),
    meta: { requiresAuth: true, layout: 'onboarding', titleKey: 'onboarding.pageTitle' },
  },
  {
    path: '/page-builder/:page?',
    name: 'page-builder',
    component: () => import('@/pages/page-builder/PageBuilderPage.vue'),
    props: true,
    meta: { requiresAuth: true, layout: 'blank', titleKey: 'page_builder.title' },
  },
  {
    path: '/',
    component: () => import('@/app/layouts/AdminShell.vue'),
    meta: { requiresAuth: true, layout: 'admin' },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/pages/dashboard/DashboardHome.vue'),
        meta: { titleKey: 'nav.dashboard', icon: 'LayoutDashboard' },
      },
      {
        path: 'products',
        name: 'products',
        component: () => import('@/pages/products/ProductsListPage.vue'),
        meta: {
          titleKey: 'nav.products',
          icon: 'ShoppingBag',
        },
      },
      {
        path: 'smart-collections',
        name: 'smart-collections',
        component: () => import('@/pages/products/SmartCollectionsPage.vue'),
        meta: {
          titleKey: 'smartCollections.pageTitle',
          icon: 'Layers',
        },
      },
      {
        path: 'products/create',
        name: 'product-create',
        component: () => import('@/pages/products/ProductCreatePage.vue'),
        meta: {
          titleKey: 'products.createProduct.title',
          icon: 'ShoppingBag',
          hidden: true,
        },
      },
      {
        path: 'products/:id',
        name: 'product-detail',
        component: () => import('@/pages/products/ProductDetailPage.vue'),
        meta: {
          titleKey: 'products.detail.title',
          hidden: true,
        },
      },
      {
        path: 'products/:id/edit',
        name: 'product-edit',
        component: () => import('@/pages/products/ProductEditPage.vue'),
        meta: {
          titleKey: 'products.editProduct.title',
          icon: 'ShoppingBag',
          hidden: true,
        },
      },
      {
        path: 'categories',
        name: 'categories',
        component: () => import('@/pages/categories/CategoryListPage.vue'),
        meta: {
          titleKey: 'nav.categories',
          icon: 'FolderOpen',
        },
      },
      {
        path: 'categories/create',
        name: 'category-create',
        component: () => import('@/pages/categories/CategoryCreatePage.vue'),
        meta: {
          titleKey: 'categories.create.title',
          hidden: true,
        },
      },
      {
        path: 'categories/:id/edit',
        name: 'category-edit',
        component: () => import('@/pages/categories/CategoryEditPage.vue'),
        meta: {
          titleKey: 'categories.edit.title',
          hidden: true,
        },
      },
      {
        path: 'brands',
        name: 'brands',
        component: () => import('@/pages/brands/BrandListPage.vue'),
        meta: {
          titleKey: 'nav.brands',
          icon: 'Tag',
        },
      },
      {
        path: 'brands/create',
        name: 'brand-create',
        component: () => import('@/pages/brands/BrandCreatePage.vue'),
        meta: {
          titleKey: 'brands.create.title',
          hidden: true,
        },
      },
      {
        path: 'brands/:id/edit',
        name: 'brand-edit',
        component: () => import('@/pages/brands/BrandEditPage.vue'),
        meta: {
          titleKey: 'brands.edit.title',
          hidden: true,
        },
      },
      {
        path: 'billing',
        name: 'billing',
        component: () => import('@/pages/billing/BillingPage.vue'),
        meta: {
          titleKey: 'billing.pageTitle',
          icon: 'CreditCard',
        },
      },
      {
        path: 'billing/ai-credits',
        name: 'billing-ai-credits',
        component: () => import('@/pages/billing/AiCreditsPage.vue'),
        meta: {
          titleKey: 'aiCredits.pageTitle',
          icon: 'Sparkles',
          hidden: true,
        },
      },
      // Customers
      {
        path: 'customers',
        name: 'customers',
        component: () => import('@/pages/customers/CustomersListPage.vue'),
        meta: { titleKey: 'customers.pageTitle', icon: 'Users' },
      },
      {
        path: 'customers/create',
        name: 'customer-create',
        component: () => import('@/pages/customers/CustomerCreatePage.vue'),
        meta: { titleKey: 'customers.create.title', hidden: true },
      },
      {
        path: 'customers/:id',
        name: 'customer-detail',
        component: () => import('@/pages/customers/CustomerDetailPage.vue'),
        meta: { titleKey: 'customers.detail.title', hidden: true },
      },
      {
        path: 'customers/:id/edit',
        name: 'customer-edit',
        component: () => import('@/pages/customers/CustomerEditPage.vue'),
        meta: { titleKey: 'customers.edit.title', hidden: true },
      },
      // Shipping
      {
        path: 'shipping',
        name: 'shipping',
        component: () => import('@/pages/shipping/ShippingZonesPage.vue'),
        meta: { titleKey: 'shipping.pageTitle', icon: 'Truck' },
      },
      {
        path: 'shipping/zones/create',
        name: 'shipping-zone-create',
        component: () => import('@/pages/shipping/ShippingZoneCreatePage.vue'),
        meta: { titleKey: 'shipping.create.zone.title', hidden: true },
      },
      {
        path: 'shipping/zones/:id/edit',
        name: 'shipping-zone-edit',
        component: () => import('@/pages/shipping/ShippingZoneEditPage.vue'),
        meta: { titleKey: 'shipping.edit.zone.title', hidden: true },
      },
      {
        path: 'shipping/zones/:zoneId/methods/create',
        name: 'shipping-method-create',
        component: () => import('@/pages/shipping/ShippingMethodCreatePage.vue'),
        meta: { titleKey: 'shipping.create.method.title', hidden: true },
      },
      {
        path: 'shipping/zones/:zoneId/methods/:methodId/edit',
        name: 'shipping-method-edit',
        component: () => import('@/pages/shipping/ShippingMethodEditPage.vue'),
        meta: { titleKey: 'shipping.edit.method.title', hidden: true },
      },
      // Discounts
      {
        path: 'discounts',
        name: 'discounts',
        component: () => import('@/pages/discounts/DiscountsListPage.vue'),
        meta: { titleKey: 'discounts.pageTitle', icon: 'Tag' },
      },
      {
        path: 'discounts/create',
        name: 'discount-create',
        component: () => import('@/pages/discounts/DiscountCreatePage.vue'),
        meta: { titleKey: 'discounts.create.title', hidden: true },
      },
      {
        path: 'discounts/:id/edit',
        name: 'discount-edit',
        component: () => import('@/pages/discounts/DiscountEditPage.vue'),
        meta: { titleKey: 'discounts.edit.title', hidden: true },
      },

      // Checkout
      {
        path: 'checkout-configuration',
        name: 'checkout-config',
        component: () => import('@/pages/checkout/CheckoutConfigPage.vue'),
        meta: { titleKey: 'nav.checkout', icon: 'ShoppingCart' },
      },
      {
        path: 'legal-pages',
        name: 'legal-pages',
        component: () => import('@/pages/legal-pages/LegalPagesPage.vue'),
        meta: { titleKey: 'legalPages.pageTitle', icon: 'FileText' },
      },

      // Orders
      {
        path: 'orders',
        name: 'orders',
        component: () => import('@/pages/orders/OrdersListPage.vue'),
        meta: { titleKey: 'orders.pageTitle', icon: 'ShoppingBag' },
      },
      {
        path: 'orders/:id',
        name: 'order-detail',
        component: () => import('@/pages/orders/OrderDetailPage.vue'),
        meta: { titleKey: 'orders.detail.title', hidden: true },
      },
    ],
  },
  {
    path: '/forbidden',
    name: 'forbidden',
    component: () => import('@/pages/ForbiddenPage.vue'),
    meta: { layout: 'blank', titleKey: 'errors.pages.forbidden.title' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/pages/NotFoundPage.vue'),
    meta: { layout: 'blank', titleKey: 'errors.pages.notFound.title' },
  },
]
